import React, { useContext, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Collapse,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  ArrowBackIos as ArrowBackIosIcon,
  QrCode as QrCodeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';           // ← QR generator
import { useQuery, useMutation } from '@apollo/client';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { UserContext } from '../../../../platform/components/auth/auth-context';
import { GET_STAFF_BY_URL, GET_BUSINESS_BY_URL } from '../../../../platform/graphql/queries';
import { GET_WALLET_BALANCE, GET_TIP_ITEMS } from '../../../../platform/graphql/queries/tip';
import { TIP_WITH_PLANY_COINS } from '../../../../platform/graphql/mutations/tip';
import { SEND_EMAIL_MUTATION } from '../../../../platform/graphql/mutations';
import { useSelectedBusiness } from '../../../../platform/redux/store';

/* ───── Constants ───── */
const PURPOSE_OPTIONS = [
  { label: 'Support (no message)', value: 'support' },
  { label: 'Add a custom message…', value: 'custom' },
];

export default function TipPage() {
  /* ── Router / context ── */
  const { businessUrl, staffUrl } = useParams();
  const navigate = useNavigate();
  const { providerUser } = useContext(UserContext);

  /* ── Local state ── */
  const [open, setOpen] = useState(false);        // tip dialog
  const [selectedItem, setSelectedItem] = useState(null);
  const [purpose, setPurpose] = useState('support');
  const [note, setNote] = useState('');
  const [copiedStaff, setCopiedStaff] = useState(false);
  const [copiedBusiness, setCopiedBusiness] = useState(false);
  const [qrVisible, setQrVisible] = useState(false);  // ← QR toggle
  const selectedBusiness = useSelectedBusiness();
  /* ── Apollo queries ── */
  const {
    data: businessData,
    loading: businessLoading,
    error: businessError,
  } = useQuery(GET_BUSINESS_BY_URL, {
    variables: { businessUrl },
    skip: !businessUrl,
  });
  const business = businessData?.businessByUrl;

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useQuery(GET_STAFF_BY_URL, {
    variables: { businessUrl, staffUrl },
    skip: !staffUrl,
  });
  const staff = staffData?.staffByUrl;

  const { data: itemsData, loading: itemsLoading } = useQuery(GET_TIP_ITEMS);

  const staffId = providerUser?.staffId;
  const { data: walletData, loading: walletLoading } = useQuery(
    GET_WALLET_BALANCE,
    { variables: { staffId }, skip: !staffId }
  );

  /* ── Mutations ── */
  const [tipWithCoins, { loading: tipping }] = useMutation(
    TIP_WITH_PLANY_COINS,
    { refetchQueries: [{ query: GET_WALLET_BALANCE, variables: { staffId } }] }
  );
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  /* ── Early returns ── */
  if (businessLoading || staffLoading || itemsLoading || walletLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  if (businessError || staffError) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading business or staff info.</Typography>
      </Box>
    );
  }

  /* ── Derived helpers ── */
  const isStaffPage = Boolean(staffUrl);
  if (isStaffPage && !staff) {
    return (
      <Box p={3}>
        <Typography color="error">Staff not found.</Typography>
      </Box>
    );
  }

  const userName = isStaffPage ? staff.user.username : business.name;
  const coinBalance = walletData?.getPlanyCoinWallet?.balance ?? 0;
  const isCreator =
    isStaffPage &&
    providerUser.staffId &&
    Number(providerUser.staffId) === Number(staff.id);

  /* Tip URLs */
  const fullTipUrlStaff = `${window.location.origin}/${businessUrl}/${staffUrl}/tip`;
  const fullTipUrlBusiness = `${window.location.origin}/${businessUrl}/tip`;
  const tipPageUrl = window.location.href;            // used for QR

  /* Wallet path for back button */
  const myWalletPath = selectedBusiness?.businessBasic?.url
    ? `/${selectedBusiness.businessBasic.url}/${providerUser?.url}/wallet`
    : '/login';

  /* ── Re‑usable helpers ── */
  const copyUrl = (url, setter) => {
    navigator.clipboard.writeText(url).then(() => {
      setter(true);
      setTimeout(() => setter(false), 2000);
    });
  };

  const handleTipClick = item => {
    setSelectedItem(item);
    setPurpose('support');
    setNote('');
    setOpen(true);
  };

  /* Confirm tip (unchanged from your original) */
  const handleConfirmTip = async () => {
    if (!selectedItem) return;

    /* 1️⃣ Build note */
    const trimmedNote = note.trim();
    const prefixedNote = trimmedNote ? `${purpose}: ${trimmedNote}` : `${purpose}`;

    /* 2️⃣ Mutation input */
    const input = { itemId: selectedItem.id };
    if (prefixedNote) input.note = prefixedNote;
    if (isStaffPage) input.targetStaffId = staff.id;
    else input.targetBusinessId = business.id;

    await tipWithCoins({ variables: { input } });

    /* 3️⃣ E‑mail context */
    const recipientEmail = isStaffPage ? staff.user.email : business.emails[0];
    const recipientName = isStaffPage ? staff.user.username : business.name;
    const senderEmail = providerUser.email;
    const senderName = providerUser.username;
    const coinAmount = selectedItem.price;
    const itemLabel = selectedItem.name;
    const plainMsg = prefixedNote ? `\nMessage: "${prefixedNote}"` : '';
    const htmlMsg = prefixedNote ? `<p>Message: "${prefixedNote}"</p>` : '';

    /* 5️⃣ Notify recipient */
    sendEmail({
      variables: {
        from_email: 'no-reply@plany365.com',
        to: recipientEmail,
        subject: `${senderName} just tipped you ${coinAmount} PlanyCoins!`,
        text: `${senderName} sent you the ${itemLabel} tip (${coinAmount} coins).${plainMsg}`,
        html: `<p>${senderName} just tipped you <strong>${coinAmount} PlanyCoins</strong> (${itemLabel}).</p>${htmlMsg}`,
      },
    }).catch(console.error);

    /* 6️⃣ Confirmation to sender */
    sendEmail({
      variables: {
        from_email: 'no-reply@plany365.com',
        to: senderEmail,
        subject: `Thanks for tipping ${recipientName}!`,
        text: `You sent ${coinAmount} PlanyCoins to ${recipientName}.${plainMsg}`,
        html: `<p>Thanks for supporting <strong>${recipientName}</strong> with <strong>${coinAmount} PlanyCoins</strong> (${itemLabel}).</p>${htmlMsg}`,
      },
    }).catch(console.error);

    alert('🎉 Tip sent!');
    setOpen(false);
  };

  /* Group items by tier */
  const grouped = { Micro: [], Standard: [], Premium: [], Elite: [], Mega: [] };
  itemsData.getTipItems.forEach(item => {
    if (item.price <= 3) grouped.Micro.push(item);
    else if (item.price <= 15) grouped.Standard.push(item);
    else if (item.price <= 100) grouped.Premium.push(item);
    else if (item.price <= 500) grouped.Elite.push(item);
    else grouped.Mega.push(item);
  });
  const tierOrder = ['Mega', 'Elite', 'Premium', 'Standard', 'Micro'];

  /* ── Render ── */
  return (
    <Box p={3} sx={{ position: 'relative' }}>
      {/* QR toggle */}
      <Tooltip title={qrVisible ? 'Hide QR code' : 'Show QR code'} arrow>
        <IconButton
          onClick={() => setQrVisible(v => !v)}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1300 }}
          size="large"
        >
          {qrVisible ? <CloseIcon /> : <QrCodeIcon />}
        </IconButton>
      </Tooltip>

      {/* Collapsible QR card */}
      <Collapse in={qrVisible} unmountOnExit>
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: 56,
            right: 8,
            p: 2,
            borderRadius: 2,
            zIndex: 1299,
          }}
        >
          <QRCodeSVG value={tipPageUrl} size={160} />
          <Typography variant="caption" align="center" sx={{ mt: 1 }}>
            Scan to tip
          </Typography>
        </Paper>
      </Collapse>

      {/* Back button */}
      <Button
        startIcon={<ArrowBackIosIcon />}
        onClick={() => navigate(myWalletPath)}
        sx={{ mb: 2 }}
      >
        Back to Wallet
      </Button>

      <Typography variant="h5" gutterBottom>
        {isCreator ? 'Your Tip Page' : `💡 Tip ${userName}`}
      </Typography>

      {/* Creator‑only link block */}
      {isCreator && isStaffPage && (
        <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
          <Typography fontWeight="bold">Personal Tip URL</Typography>
          <Box display="flex" alignItems="center" mb={1}>
            <MuiLink
              href={fullTipUrlStaff}
              sx={{ wordBreak: 'break-all', fontWeight: 500 }}
            >
              {fullTipUrlStaff}
            </MuiLink>
            <IconButton size="small" onClick={() => copyUrl(fullTipUrlStaff, setCopiedStaff)}>
              {copiedStaff ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Typography fontWeight="bold">Business Tip URL</Typography>
          <Box display="flex" alignItems="center">
            <MuiLink
              href={fullTipUrlBusiness}
              sx={{ wordBreak: 'break-all', fontWeight: 500 }}
            >
              {fullTipUrlBusiness}
            </MuiLink>
            <IconButton size="small" onClick={() => copyUrl(fullTipUrlBusiness, setCopiedBusiness)}>
              {copiedBusiness ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Typography sx={{ mt: 1, color: '#00695c' }}>
            Copy &amp; share these links on your channel, website, or social media.
          </Typography>
        </Paper>
      )}
 
      {/* Coin balance (viewer) */}
      {!isCreator && !qrVisible && (                 /* ← hide when QR panel is open */
        <Box sx={{ mb: 4 }}>
          <Typography>
            🪙 You have <strong>{coinBalance}</strong> PlanyCoins
            <Button component={Link} to="/coins/buy" sx={{ ml: 1 }}>
              ➕ Buy More
            </Button>
          </Typography>
        </Box>
      )}


      {/* Tip buttons */}
      {tierOrder.map(tier => (
        <Box key={tier} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{tier}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {grouped[tier].map(item => {
              const disabled = tipping || coinBalance < item.price || isCreator;
              const tooltipTitle = isCreator
                ? 'You cannot tip yourself'
                : coinBalance < item.price
                  ? 'Not enough PlanyCoins'
                  : '';
              return (
                <Tooltip key={item.id} title={tooltipTitle} arrow disableHoverListener={!tooltipTitle}>
                  <span>
                    <Button
                      onClick={() => handleTipClick(item)}
                      disabled={disabled}
                      variant="outlined"
                      sx={{
                        width: 90,
                        height: 90,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h4" component="span">{item.emoji}</Typography>
                      <Typography variant="caption">{item.name}</Typography>
                      <Typography variant="subtitle2" fontWeight="bold">{item.price} 🪙</Typography>
                    </Button>
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}

      {/* Tip dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Customize Your Tip</DialogTitle>
        <DialogContent>
          <Select
            fullWidth
            value={purpose}
            onChange={e => setPurpose(e.target.value)}
            sx={{ mb: 2 }}
          >
            {PURPOSE_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {purpose === 'custom' && (
            <TextField
              multiline
              rows={3}
              fullWidth
              label="Message"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. Say hi!"
              sx={{ mb: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmTip}
            variant="contained"
            disabled={!selectedItem || tipping || coinBalance < (selectedItem?.price || 0)}
          >
            {tipping ? <CircularProgress size={20} /> : 'Confirm Tip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
